import React, { useState, useCallback } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';
import { appSelectors } from 'store/app';
import styled from 'styled-components/macro';
import scaleSize from 'utils/scale-size';
import dispatchKeyboardEvent from 'utils/dispatch-keyboard-event';
import config from 'config';
import { FocusableParent } from 'components/navigation';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import MenuItem from './MenuItem';

const MenuWrapper = styled(FocusableParent)`
  width: ${scaleSize(540)}px;
  height: ${scaleSize(1080)}px;
  background: ${({ theme }) => theme.menuBackground};
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  transform: translate3d(${({ isFocused }) => (isFocused ? 0 : -scaleSize(440))}px, 0, 0);
  transition: ${config.withAnimation ? `transform ${config.mainTransitionDuration}` : 'none'};
  overflow: hidden;
`;

const Overlay = styled.div`
  width: ${scaleSize(1920)}px;
  height: ${scaleSize(1080)}px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  background: ${({ theme }) => theme.overlayColor};
  opacity: ${({ isFocused }) => (isFocused ? 1 : 0)};
  transform: translate3d(${({ isFocused }) => (isFocused ? 0 : -scaleSize(1920))}px, 0, 0);
  transition: ${config.withAnimation ? `opacity ${config.mainTransitionDuration}` : 'none'};
`;

const MenuItemsWrapper = styled.div`
  width: ${scaleSize(540)}px;
  position: absolute;
  top: 50%;
  transform: translate3d(0, -50%, 0);

  span {
    opacity: ${({ isFocused }) => +isFocused};
  }
`;

const SECTION_NAME = 'MAIN_MENU';

const Menu = ({ leaveForRight }) => {
  const history = useHistory();
  const match = useRouteMatch();
  const [isFocused, setIsFocused] = useState(false);
  const menuItems = useSelector(appSelectors.selectMenuItems);

  const openMenu = useCallback(
    () => !isFocused && SpatialNavigationWrapper.focus(`@${SECTION_NAME}`),
    [isFocused],
  );
  const closeMenu = useCallback(
    () => dispatchKeyboardEvent({ keyCode: 39, key: 'ArrowRight', which: 39 }),
    [],
  );
  const onFocus = useCallback(() => setIsFocused(true), []);
  const onBlur = useCallback(() => setIsFocused(false), []);
  const onEnter = useCallback(
    (item) => {
      const isTheSamePath = match.url === item.path;

      if (!isFocused || isTheSamePath) return closeMenu();

      history.push(item.path);
    },
    [match.url, isFocused, closeMenu, history],
  );

  return (
    <>
      <Overlay isFocused={isFocused} onClick={closeMenu} />
      {menuItems && (
        <MenuWrapper
          sectionName={SECTION_NAME}
          id={SECTION_NAME}
          config={{
            preventMouseNavigateIn: true,
            preventMouseNavigateWithin: true,
            leaveFor: {
              up: '',
              down: '',
              left: '',
              right: leaveForRight,
            },
            defaultElement: `#${MenuItem.ACTION_ITEM_ID}`,
            enterTo: 'default-element',
          }}
          isFocused={isFocused}
          onClick={openMenu}
        >
          <MenuItemsWrapper isFocused={isFocused}>
            {menuItems.map((item, i) => (
              <MenuItem
                isMenuFocused={isFocused}
                onFocus={onFocus}
                onBlur={onBlur}
                onEnter={onEnter}
                item={item}
                isActive={new RegExp(`^${item.path}$`).test(match.url)}
                key={`menu-item-${i}`}
              />
            ))}
          </MenuItemsWrapper>
        </MenuWrapper>
      )}
    </>
  );
};

export default Menu;
